import { DepressionContentPage } from "@/components/DepressionContentPage";

const DepressionTreatmentCost = () => (
  <DepressionContentPage
    seo={{
      title:
        "Depression Treatment Cost & Insurance | Neuro Wellness TMS Centers of America",
      description:
        "Understand the cost of advanced depression treatment — TMS, SAINT®, SPRAVATO®, and ketamine. Learn how insurance, deductibles, and self-pay options work.",
      canonical: "/depression-treatment-cost",
      keywords:
        "depression treatment cost, TMS cost, SAINT cost, SPRAVATO cost, insurance verification, self-pay, Coral Springs",
    }}
    badge="Cost & Insurance"
    heroTitle="Understanding the Cost of Advanced Depression Treatment"
    heroSubtitle="Cost is a real concern, and we don't hide from it. The cost of treatment depends on several factors — the recommended treatment, the number of sessions, your insurance, and your benefits. Here's how to think about it."
    sections={[
      {
        heading: "What Cost Depends On",
        variant: "cards",
        cards: [
          {
            title: "Recommended Treatment",
            body: "Different treatments have different structures and costs.",
          },
          {
            title: "Number of Sessions",
            body: "Some treatments involve a fixed course; others vary based on response.",
          },
          {
            title: "Insurance Coverage",
            body: "Whether your plan covers the treatment and to what extent.",
          },
          {
            title: "Medical Necessity",
            body: "Insurance typically requires documentation of medical necessity.",
          },
          {
            title: "Deductible & Coinsurance",
            body: "Your share of cost after insurance benefits apply.",
          },
          {
            title: "Self-Pay Options",
            body: "For those without coverage or preferring to pay directly.",
          },
        ],
      },
      {
        heading: "Three Pathways to Understanding Your Cost",
        variant: "text",
        body: (
          <p>
            Everyone's situation is different. Choose the path that fits yours,
            and our team can help you understand what your cost may look like
            before you commit to anything.
          </p>
        ),
      },
      {
        heading: "I'm Using Insurance",
        variant: "callout",
        callout:
          "If you plan to use insurance, the first step is verifying your benefits. Our team can review your plan, check medical necessity requirements, and handle prior authorization on your behalf. We'll tell you what your out-of-pocket cost is likely to be before treatment begins.",
      },
      {
        heading: "I'm Considering Self-Pay",
        variant: "callout",
        callout:
          "If you're paying privately or your plan doesn't cover a particular treatment, we can discuss self-pay options. We'll give you clear information about cost so you can make an informed decision.",
      },
      {
        heading: "I'm Comparing Treatment Options",
        variant: "callout",
        callout:
          "If you're still deciding which treatment may be right, we can discuss the cost differences between options as part of your consultation. Cost is one factor among several, and it's worth understanding alongside clinical considerations.",
      },
    ]}
    faq={[
      {
        question: "Will my insurance cover treatment?",
        answer:
          "Coverage varies by plan, treatment, and medical necessity requirements. Some plans cover TMS and SPRAVATO® with prior authorization. We verify your benefits before any financial commitment.",
      },
      {
        question: "Can you tell me my cost before I start?",
        answer:
          "Yes. We verify your benefits and provide you with an estimate of your out-of-pocket cost before treatment begins. You won't be surprised by an unexpected bill.",
      },
      {
        question: "Do you offer financing or payment plans?",
        answer:
          "We can discuss financing and payment options during your consultation. Our goal is to make treatment as accessible as possible.",
      },
      {
        question: "What if I don't have insurance?",
        answer:
          "We offer self-pay options. Contact our team to discuss pricing and payment arrangements that may work for your situation.",
      },
    ]}
    relatedLinks={[
      {
        title: "Insurance for TMS & Depression Treatment",
        href: "/insurance-for-tms-depression-treatment",
      },
      {
        title: "Compare Depression Treatments",
        href: "/compare-treatments",
      },
      {
        title: "Depression Treatment Options",
        href: "/depression-treatment-options",
      },
      {
        title: "What Happens During a Consultation",
        href: "/depression-treatment-consultation",
      },
    ]}
    finalCtaHeading="Get Treatment & Cost Information"
    finalCtaText="Don't try to figure out the cost alone. Our team can verify your benefits and explain your options clearly. Call us to start the conversation."
  />
);

export default DepressionTreatmentCost;
